import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailBackendApi } from '@/lib/email/edge-functions-api';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from './useWorkspaces';
import { useToast } from './use-toast';
import { captureError } from '@/lib/sentry';
import type {
  SafetyControl,
  CampaignCreationRequest,
} from '@/lib/email/edge-functions-api';

// Shape returned by the public.get_queue_health(uuid) RPC.
// System-wide fields are NULL for non-super-admin callers.
export interface QueueHealth {
  // System-wide (super-admin only; null otherwise)
  last_run_started_at: string | null;
  last_run_completed_at: string | null;
  last_run_status: string | null;
  last_run_duration_ms: number | null;
  last_run_jobs_fetched: number | null;
  last_run_jobs_succeeded: number | null;
  last_run_jobs_failed: number | null;
  cron_job_active: boolean | null;
  recover_cron_last_run_at: string | null;
  recover_cron_last_status: string | null;
  system_overdue_count: number | null;
  system_oldest_overdue_minutes: number | null;
  system_total_pending: number | null;
  system_processing_count: number | null;
  // Workspace-scoped (always populated for workspace members)
  ws_overdue_count: number;
  ws_total_pending: number;
  ws_processing_count: number;
  ws_sent_1h: number;
  ws_sent_24h: number;
  ws_active_campaigns: number;
}

const STALE_TIME = 30 * 1000; // 30 seconds for real-time data
const PROGRESS_STALE_TIME = 5 * 1000; // 5 seconds for progress updates

// Health & Monitoring Hooks
export function useEmailBackendHealth() {
  return useQuery({
    queryKey: ['email-backend', 'health'],
    queryFn: () => emailBackendApi.healthCheck(),
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  });
}

// Campaign Management Hooks
export function useEmailCampaigns() {
  const { currentWorkspace } = useWorkspaces();

  return useQuery({
    queryKey: ['email-backend', 'campaigns', currentWorkspace?.id],
    queryFn: () => emailBackendApi.getCampaigns(currentWorkspace!.id),
    enabled: !!currentWorkspace?.id,
    staleTime: STALE_TIME,
  });
}

export function useEmailCampaignDetails(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['email-backend', 'campaign', campaignId],
    queryFn: () => emailBackendApi.getCampaignDetails(campaignId!),
    enabled: !!campaignId,
    staleTime: STALE_TIME,
  });
}

export function useEmailCampaignProgress(campaignId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['email-backend', 'campaign-progress', campaignId],
    queryFn: () => emailBackendApi.getCampaignProgress(campaignId!),
    enabled: !!campaignId && enabled,
    staleTime: PROGRESS_STALE_TIME,
    refetchInterval: (query) => {
      // Auto-refresh for active campaigns
      const campaign = query.state.data?.data;
      if (campaign?.current_status === 'sending' || campaign?.current_status === 'processing') {
        return 10000; // 10 seconds
      }
      return false;
    },
  });
}

export function useEmailCampaignTimeline(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['email-backend', 'campaign-timeline', campaignId],
    queryFn: () => emailBackendApi.getCampaignTimeline(campaignId!),
    enabled: !!campaignId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Campaign Control Mutations
export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignData: CampaignCreationRequest) =>
      emailBackendApi.createCampaign(campaignData),
    onSuccess: (response) => {
      // Only invalidate queries if the backend call was successful
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['email-backend', 'campaigns'] });
      }
      return response;
    },
    // Let the component handle errors manually for CORS fallback
    throwOnError: false,
  });
}

export function usePauseCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ campaignId }: { campaignId: string; reason?: string }) =>
      emailBackendApi.pauseCampaign(campaignId),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'campaign', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'campaign-progress', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'campaigns'] });
      
      toast({
        title: "Campaign Paused",
        description: "The email campaign has been paused successfully",
      });
    },
    onError: (error) => {
      captureError(error as Error, { feature: 'campaign-pause' });
      toast({
        title: "Error",
        description: "Failed to pause campaign",
        variant: "destructive",
      });
    },
  });
}

export function useResumeCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (campaignId: string) => {
      // resumeCampaign reports failure as {success:false} rather than throwing.
      const res = await emailBackendApi.resumeCampaign(campaignId);
      if (!res.success) throw new Error(res.error || 'Failed to resume campaign');
      return res;
    },
    onSuccess: (_, campaignId) => {
      // The campaign pages read the hermes keys (useHermesCampaigns).
      queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['hermes-campaign', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'campaign', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'campaign-progress', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'campaigns'] });
      
      toast({
        title: "Campaign Resumed",
        description: "The email campaign has been resumed successfully",
      });
    },
    onError: (error) => {
      captureError(error as Error, { feature: 'campaign-resume' });
      toast({
        title: "Error",
        description: error.message || "Failed to resume campaign",
        variant: "destructive",
      });
    },
  });
}

// Queue Management Hooks
export function useQueueStats() {
  return useQuery({
    queryKey: ['email-backend', 'queue-stats'],
    queryFn: () => emailBackendApi.getQueueStats(),
    staleTime: STALE_TIME,
    refetchInterval: 15000,
  });
}

export function useQueueJobs(campaignId: string | undefined, status?: string) {
  const { currentWorkspace } = useWorkspaces();

  return useQuery({
    queryKey: ['email-backend', 'queue-jobs', campaignId || currentWorkspace?.id, status],
    queryFn: () => {
      if (campaignId) {
        return emailBackendApi.getCampaignJobs(campaignId, status);
      }
      return emailBackendApi.getWorkspaceJobs(currentWorkspace!.id, status);
    },
    enabled: !!(campaignId || currentWorkspace?.id),
    staleTime: STALE_TIME,
    refetchInterval: 15000,
  });
}

export function useCampaignJobs(campaignId: string | undefined, status?: string) {
  return useQuery({
    queryKey: ['email-backend', 'queue-jobs', campaignId, status],
    queryFn: () => emailBackendApi.getCampaignJobs(campaignId!, status),
    enabled: !!campaignId,
    staleTime: STALE_TIME,
    refetchInterval: 15000,
  });
}

export function useOverdueJobs() {
  return useQuery({
    queryKey: ['email-backend', 'overdue-jobs'],
    queryFn: () => emailBackendApi.getOverdueJobs(),
    staleTime: 60 * 1000,
    refetchInterval: 30000,
  });
}

export function useRetryFailedJobs() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (campaignId?: string) => emailBackendApi.retryFailedJobs(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'queue-stats'] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'queue-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'overdue-jobs'] });
      
      toast({
        title: "Jobs Retried",
        description: "Failed jobs have been queued for retry",
      });
    },
    onError: (error) => {
      captureError(error as Error, { feature: 'queue-retry-failed-jobs' });
      toast({
        title: "Error",
        description: "Failed to retry jobs",
        variant: "destructive",
      });
    },
  });
}

// Safety System Hooks
export function useSafetyStatus() {
  return useQuery({
    queryKey: ['email-backend', 'safety-status'],
    queryFn: () => emailBackendApi.getSafetyStatus(),
    staleTime: STALE_TIME,
    refetchInterval: 30000, // Check every 30 seconds
  });
}

export function useSafetyControls() {
  return useQuery({
    queryKey: ['email-backend', 'safety-controls'],
    queryFn: () => emailBackendApi.getSafetyControls(),
    staleTime: STALE_TIME,
  });
}

export function useRecentAnomalies(hours = 24) {
  return useQuery({
    queryKey: ['email-backend', 'anomalies', hours],
    queryFn: () => emailBackendApi.getRecentAnomalies(hours),
    staleTime: STALE_TIME,
  });
}

export function useCreateSafetyControl() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ 
      type, 
      reason, 
      targetId 
    }: { 
      type: SafetyControl['type']; 
      reason: string; 
      targetId?: string; 
    }) => emailBackendApi.createSafetyControl(type, reason, targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'safety-controls'] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'safety-status'] });
      
      toast({
        title: "Safety Control Activated",
        description: "The safety control has been successfully activated",
      });
    },
    onError: (error) => {
      captureError(error as Error, { feature: 'safety-control-create' });
      toast({
        title: "Error",
        description: "Failed to create safety control",
        variant: "destructive",
      });
    },
  });
}

export function useDeactivateSafetyControl() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (controlId: string) => {
      const response = await emailBackendApi.deactivateSafetyControl(controlId);
      if (!response.success) {
        // Surface the real error from the edge function (e.g.
        // 'forbidden_workspace_admin_required') so the toast and GlitchTip
        // see the actual reason rather than a generic wrapper.
        throw new Error(response.error || 'Failed to deactivate safety control');
      }
      return response;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'safety-controls'] });
      queryClient.invalidateQueries({ queryKey: ['email-backend', 'safety-status'] });
      // Refresh hermes_email_accounts views — emergency_pause_active may have
      // been cleared by the edge function for account_pause controls.
      // SenderSettings refetches email_providers via its own fetchProviders()
      // call site, not React Query, so no 'email-providers' key invalidation
      // is wired up at the data layer.
      queryClient.invalidateQueries({ queryKey: ['hermes-email-accounts'] });

      toast({
        title: "Safety Control Deactivated",
        description: response.data?.account_flag_cleared
          ? "Control deactivated and the account has been unblocked."
          : "The safety control has been successfully deactivated",
      });
    },
    onError: (error) => {
      captureError(error as Error, { feature: 'safety-control-deactivate' });
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to deactivate safety control",
        variant: "destructive",
      });
    },
  });
}


// Queue / cron liveness health (Task #24).
// Polls public.get_queue_health(p_workspace_id) every 30s, matching
// useSafetyStatus()'s cadence so the Admin tab refreshes coherently.
// Workspace-scoped fields are always populated for any workspace member;
// system-wide fields are populated only when the caller is a super-admin.
export function useQueueHealth(workspaceIdOverride?: string) {
  const { currentWorkspace } = useWorkspaces();
  const workspaceId = workspaceIdOverride ?? currentWorkspace?.id;

  return useQuery({
    queryKey: ['email-backend', 'queue-health', workspaceId],
    queryFn: async (): Promise<QueueHealth | null> => {
      if (!workspaceId) return null;
      const { data, error } = await supabase.rpc('get_queue_health', {
        p_workspace_id: workspaceId,
      });
      if (error) throw error;
      // RPC returns SETOF (one row); supabase-js delivers it as an array.
      const row = Array.isArray(data) ? data[0] : data;
      return (row as QueueHealth) ?? null;
    },
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
    refetchInterval: 30000,
  });
}
